import React, { useEffect, useMemo, useState } from 'react'
import BookingSubtitle from '../../../Share/Booking/BookingSubtitle/BookingSubtitle'
import { useMall, useReservation, useValidation } from '../../../../store'

import './FixedScheduleDetailForm.scss'

const guestNameItem = idx => `동반인${idx} 성함`
const guestMenuItem = idx => `동반인${idx} 점심 메뉴`

// 고정 일정 매장의 정보 입력 (참석자별 점심 메뉴, 요청사항, 진행 조건 확인, 개인정보 동의)
// 본인 메뉴·진행 조건·개인정보 동의는 매장 추가정보에 값을 넣고, 동반인 성함/메뉴는 추가정보 항목을 새로 붙여 전송
export default function FixedScheduleDetailForm({ detail }) {
	const { mall } = useMall()
	const { totalPartySize, additionalInfos, request, setReservation } = useReservation()
	const { setValidation } = useValidation()

	const baseItems = useMemo(() => (mall.info_additional || []).filter(item => item.enabled), [mall])
	const menuItem = baseItems.find(item => item.name === detail.menuItemName)
	const menuOptions = (menuItem?.validations || []).filter(item => item.enabled).map(item => item.name)

	// 결제 단계에서 돌아온 경우에만 입력값 복원 (최초 진입 시 추가정보에는 첫 번째 선택지가 기본값으로 들어가 있음)
	const isReturning = Boolean(sessionStorage.getItem('visitedBookingPayment'))
	const savedValue = name => (isReturning
		? additionalInfos?.find(item => item.name === name)?.value
		: undefined)
	const [myMenu, setMyMenu] = useState(() => (menuOptions.includes(savedValue(detail.menuItemName))
		? savedValue(detail.menuItemName)
		: ''))
	const [guests, setGuests] = useState(() => Array.from({ length: Math.max(totalPartySize - 1, 0) }, (_, idx) => ({
		name: savedValue(guestNameItem(idx + 1)) || '',
		menu: savedValue(guestMenuItem(idx + 1)) || '',
	})))
	const [conditionChecked, setConditionChecked] = useState(savedValue(detail.conditionItemName) === true)
	const [privacy, setPrivacy] = useState(savedValue(detail.privacyItemName) === '동의'
		? '동의'
		: '')

	const setGuest = (idx, key, value) => setGuests(prev => prev.map((guest, i) => (i === idx
		? { ...guest, [key]: value }
		: guest)))

	useEffect(() => {
		const valueOf = (item) => {
			switch (item.name) {
			case detail.menuItemName: return myMenu
			case detail.conditionItemName: return conditionChecked
			case detail.privacyItemName: return privacy
			default: return item.value
			}
		}
		const guestItems = guests.flatMap((guest, idx) => [
			guest.name && { kind: 'text', name: guestNameItem(idx + 1), choice: '', multiple: false, enabled: true, value: guest.name },
			guest.menu && {
				kind: 'select',
				name: guestMenuItem(idx + 1),
				choice: menuItem?.choice || '',
				multiple: false,
				enabled: true,
				value: guest.menu,
			},
		]).filter(Boolean)

		setReservation('additionalInfos', [
			...baseItems.map(item => ({ ...item, value: valueOf(item) })),
			...guestItems,
		])

		if (!myMenu)
			setValidation('additionalInfo', '본인 점심 메뉴를 선택해주세요.')
		else if (!conditionChecked)
			setValidation('additionalInfo', '진행 조건을 확인해주세요.')
		else if (!privacy)
			setValidation('additionalInfo', '개인정보 수집 및 마케팅 활용 동의를 선택해주세요.')
		else
			setValidation('additionalInfo', true)
	}, [myMenu, JSON.stringify(guests), conditionChecked, privacy, baseItems])

	const renderMenuSelect = (value, onChange, placeholder) => (
		<select className={value
			? 'menu-select'
			: 'menu-select empty'} value={value} onChange={e => onChange(e.target.value)}>
			<option value="">{placeholder}</option>
			{menuOptions.map(option => <option key={option} value={option}>{option}</option>)}
		</select>
	)

	return (
		<div className="fixed-schedule-detail-form">
			<div className="form-section">
				<BookingSubtitle subtitle="점심 메뉴 선택" />
				<p className="help">{detail.menuHelp}</p>
				<div className="menu-box">
					{detail.menuDescriptions.map(text => <div key={text}>{text}</div>)}
				</div>
				<div className="guest-list">
					<div className="guest-row">
						<div className="who">본인<span className="req">*</span></div>
						<div className="me">{renderMenuSelect(myMenu, setMyMenu, '메뉴 선택')}</div>
					</div>
					{guests.map((guest, idx) => (
						<div className="guest-row" key={idx}>
							<div className="who">동반인 {idx + 1}<small>선택</small></div>
							<input
								type="text"
								placeholder="성함"
								maxLength={20}
								value={guest.name}
								onChange={e => setGuest(idx, 'name', e.target.value)}
							/>
							{renderMenuSelect(guest.menu, value => setGuest(idx, 'menu', value), '메뉴')}
						</div>
					))}
				</div>
			</div>

			<div className="form-section">
				<BookingSubtitle subtitle={<>요청사항 <span className="optional">(선택)</span></>} />
				<textarea
					className="request-textarea"
					placeholder={detail.requestPlaceholder}
					defaultValue={request}
					maxLength="100"
					rows="4"
					onBlur={e => setReservation('request', e.target.value.trim())}
				/>
			</div>

			<div className="form-section">
				<BookingSubtitle subtitle="진행 조건 확인" firebrick />
				<div className="notice">{detail.conditionNotice}</div>
				<label className="check">
					<input type="checkbox" checked={conditionChecked} onChange={e => setConditionChecked(e.target.checked)} />
					확인했습니다
				</label>
			</div>

			<div className="form-section">
				<BookingSubtitle subtitle={detail.privacyItemName} firebrick />
				<select className={privacy
					? 'menu-select'
					: 'menu-select empty'} value={privacy} onChange={e => setPrivacy(e.target.value)}>
					<option value="">선택</option>
					<option value="동의">동의</option>
				</select>
			</div>
		</div>
	)
}
